import * as THREE from 'three';
import { PlanogramObjectAttributes, PlanogramRendererResources } from "./Resources";
import { PlanogramObject } from "./PlanogramObject.js";
import { PlanogramContainer } from "./PlanogramContainer.js";
import { PlanogramBlockingPosition } from "./PlanogramBlockingPosition.js";
import { PlanogramRenderer } from './PlanogramRenderer.js';
import { PlanogramBlockingDivider } from './PlanogramBlockingDivider.js';

export class PlanogramBlocking extends PlanogramObject {

    static attributes = {
        "AddVerticalDivider": 'add-vertical-divider',
        "AddHorizontalDivider": 'add-horizontal-divider',
        "Disabled": 'disabled',
        "PlanogramDimensions": 'planogram-dimensions',  
        "Units": 'units'
    };


    static get observedAttributes(){
        return [
            ...Object.values(PlanogramBlocking.attributes),
            ...Object.values(PlanogramObjectAttributes.Attributes),
        ];
    }  

    get addVerticalDivider () { return this.hasAttribute(PlanogramBlocking.attributes.AddVerticalDivider); }
    get addHorizontalDivider () { return this.hasAttribute(PlanogramBlocking.attributes.AddHorizontalDivider); }
    get disabled () { return this.hasAttribute(PlanogramBlocking.attributes.Disabled); }
    get planogramDimensions () { 
        const attr = this.getAttribute(PlanogramBlocking.attributes.PlanogramDimensions);
        if(!attr) return null;
        const parts = attr.split(',').map(x=>parseFloat(x.trim()));
        if(parts.length !== 3 || parts.some(x=>isNaN(x))) return null;
        return new THREE.Vector3(parts[0], parts[1], parts[2]);
    }
    get units () { return this.getAttribute(PlanogramBlocking.attributes.Units) || 'cm'; }

    /**@type {PlanogramBlockingPosition[]} */
    get #locations(){ return [...this.querySelectorAll("planogram-blocking-location")].filter(x=>x.firstRender === false); }
    /**@type {PlanogramBlockingDivider[]} */
    get #dividers(){ return [...this.querySelectorAll("planogram-blocking-divider")].filter(x=>x.firstRender === false); }
    get #planogramSize(){ return PlanogramContainer.get(this)?.planogramBox.getSize(new THREE.Vector3()) ?? new THREE.Vector3(); }

    //#region Private Properties
    /**@type {THREE.BufferGeometry|null} */
    #geometry = null;
    /**@type {THREE.InstancedMesh|null} */
    #mesh = null;

    /**@type {THREE.Mesh|null} */
    #guideMesh = null;
    /**@type {THREE.CanvasTexture|null} */
    #guideMeshCanvasTexture = null;
    /**@type {HTMLCanvasElement|null} */
    #guideMeshCanvas = null;
    //#endregion

    get groupBoundingBox() {
        return PlanogramContainer.get(this)?.planogramBox.clone() ?? new THREE.Box3();
    } 

    constructor(){
        super();
    }

    #generateGeometry( reGeometry = false ) {
        if ( reGeometry && this.#geometry ) {
            this.#geometry.dispose();
            this.#geometry = null;
        }

        if(reGeometry || !this.#geometry){
            
            this.#geometry = new THREE.BoxGeometry(1, 1, 1);
        }
        this.#geometry.computeBoundingBox();
        this.#geometry.center();
    }

    #generateDefault(){
        const data = {
            x : 0,
            y: 0,
            width:1,
            height:1,
            colour: 0xff0000
        };
        return data;
    }

    #generateGuideMesh(){
        const padding = 10;
        const size = this.planogramDimensions ?? this.#planogramSize;
        const guideSize = this.#planogramSize.clone().add(new THREE.Vector3(padding,padding,0));
        const canvas = document.createElement('canvas');
        canvas.width = guideSize.x * 5;
        canvas.height = guideSize.y * 5;
        const context = canvas.getContext('2d');
        context.strokeStyle = '#000000';
        context.lineWidth = 0.7;
        context.font = '12px Arial';

        
        // context.strokeRect(50, 30, canvas.width-100, 20);
        context.strokeText( "25%", 0, (canvas.height / 4) * 3 );
        context.strokeText( "50%", 0, (canvas.height / 4) * 2 );
        context.strokeText( "75%", 0, (canvas.height / 4) * 1 );

        // context.strokeRect(5, 25, 20, canvas.height-50);

        // context.strokeRect(50, canvas.height-25, canvas.width-50, 20 );
        context.strokeText( "75%", (canvas.width / 4) * 3, canvas.height - 10 );
        context.strokeText( "50%", (canvas.width / 4) * 2, canvas.height - 10 );
        context.strokeText( "25%", (canvas.width / 4) * 1, canvas.height - 10 );

        // context.strokeRect( canvas.width-50, 25, 20, canvas.height-50 );

        context.save();
        context.rotate(90 * Math.PI / 180);
        context.strokeText( `${size.y}${this.units}`, canvas.height/2, -canvas.width + 20 );
        context.restore();

        if(!this.#guideMesh){
            this.#guideMeshCanvasTexture = new THREE.CanvasTexture(canvas);
            this.#guideMesh = new THREE.Mesh(new THREE.PlaneGeometry( 1, 1 ), 
                new THREE.MeshBasicMaterial( { map: this.#guideMeshCanvasTexture, transparent: true, depthWrite: false } ));
            this.addMesh(this.#guideMesh, "Guide");
        }

        this.#guideMeshCanvasTexture.image = canvas;
        this.#guideMeshCanvasTexture.needsUpdate = true;
        this.#guideMesh.geometry.computeBoundingBox();
        const boxSize =  this.#guideMesh.geometry.boundingBox.getSize(new THREE.Vector3()) ;
        this.#guideMesh.geometry.applyMatrix4( new THREE.Matrix4().makeScale( guideSize.x/boxSize.x, guideSize.y/boxSize.y, 1 ) );
        this.#guideMesh.geometry.computeBoundingBox();
        this.#guideMesh.position.set( guideSize.x/2 - padding/2, guideSize.y/2 - padding/2, 0 );
        
    }

    // Simple reentrancy guard to avoid nested planogramUpdated loops
    #isUpdating = false;
    planogramUpdated(){
        if (this.#isUpdating) return;
        this.#isUpdating = true;
        try{
            this.#generateGeometry( true );
            this.#createMeshes();
        } finally {
            this.#isUpdating = false;
        }
    }

    #createMeshes(){
        var locations = this.#locations;
        var dividers = this.#dividers;
        var isDefault = false;
        if(locations.length === 0){
            locations = [PlanogramBlockingPosition.createDefault()];
            isDefault = true;
        }
        var totalMeshCount = locations.length + dividers.length;
        var planSize = this.#planogramSize;
        var update = !this.#mesh || this.#mesh.count !== totalMeshCount && totalMeshCount > 0;
        if(update){
            this.#mesh = new THREE.InstancedMesh( this.#geometry, PlanogramRendererResources.BlockShader, totalMeshCount );
            const center = this.groupBoundingBox.getCenter(new THREE.Vector3());
            const halfSize = this.groupBoundingBox.getSize(new THREE.Vector3()).multiplyScalar(0.5);
            center.sub(halfSize);
            this.#mesh.position.copy(center);
        }
        this.#generateGuideMesh();
        var meshCount = 0;
        var sizeArray = [];
        var optionsArray = [];

        for(var divider of dividers){
            const newDimensions = divider.getDimensions(planSize);
            const newCoordinate = divider.getPosition(planSize);

            var matrix =  new THREE.Matrix4().makeTranslation( newCoordinate.x, newCoordinate.y, newCoordinate.z )
                .multiply(new THREE.Matrix4().makeScale( newDimensions.x, newDimensions.y, newDimensions.z));
            this.#mesh.setMatrixAt(meshCount++, matrix);
            sizeArray.push(...newDimensions.toArray());
            optionsArray.push(...divider.getOptions().toArray());
        }

        for(var item of locations){

            const newDimensions = (item.getDimensions ? item.getDimensions(planSize) : null) ?? planSize;
            const newCoordinate = (item.getPosition ? item.getPosition(planSize) : null) ?? new THREE.Vector3( planSize.x/2, planSize.y/2, planSize.z/2 );
            const options = (item.getOptions ? item.getOptions(isDefault) : null) ?? new THREE.Vector4( 0xff0000, 0, 0, isDefault ? 0 : 1 );
            var box = new THREE.Box3().setFromCenterAndSize(newCoordinate, newDimensions);
            dividers.reduce( (acc, divider) => {
                const itemBox = new THREE.Box3().setFromPoints([box.min, box.max]);
                const dividerBox =  new THREE.Box3().setFromCenterAndSize(divider.getPosition(planSize), divider.getDimensions(planSize));
                if(dividerBox.clone().intersectsBox(itemBox)){
                    const intersectionSize = dividerBox.clone().intersect(itemBox).getSize(new THREE.Vector3());
                    if(divider.direction === "horizontal"){
                        if(newCoordinate.y > divider.getPosition(planSize).y)
                            acc.min.y += intersectionSize.y;
                        else
                            acc.max.y -= intersectionSize.y;
                    }else{
                        if(newCoordinate.x > divider.getPosition(planSize).x)
                            acc.min.x += intersectionSize.x;
                        else
                            acc.max.x -= intersectionSize.x;
                    }
                }
                return acc;
            },{min: box.min, max:box.max} );
            var bose = new THREE.Box3().setFromPoints([box.min, box.max]);

            newDimensions.copy(bose.getSize(new THREE.Vector3()));
            newCoordinate.copy(bose.getCenter(new THREE.Vector3()));

            var matrix =  new THREE.Matrix4().makeTranslation( newCoordinate.x, newCoordinate.y, newCoordinate.z )
                .multiply(new THREE.Matrix4().makeScale( newDimensions.x, newDimensions.y, newDimensions.z ));
            this.#mesh.setMatrixAt(meshCount++, matrix);
            sizeArray.push(...newDimensions.toArray());
            optionsArray.push(...options.toArray());
        }

        

        this.#mesh.geometry.setAttribute("fillOptions", new THREE.InstancedBufferAttribute(new Float32Array(optionsArray),4));
        this.#mesh.geometry.setAttribute("size", new THREE.InstancedBufferAttribute(new Float32Array(sizeArray),3));
        var dividerLines = new Array(totalMeshCount).fill([ -1, -1, 0]);
        this.#mesh.geometry.setAttribute("dividerLines", new THREE.InstancedBufferAttribute(new Float32Array(dividerLines.flat()),3));
        this.#mesh.instanceMatrix.needsUpdate = true;
        this.#mesh.renderOrder = 10;

        const container = PlanogramContainer.get(this);
        this.applyShowPositionsThroughBlocking(container?.showPositionsThroughBlocking);
            
        if(update){
            this.addMesh(this.#mesh);
        }
        
    }

    /**
     * Updates the shader attributes for a specific instance
     * @param {number} instanceId 
     * @param {THREE.Vector2} uv
     * @param {THREE.Vector3} normal
     */
    updateShaderAttributes( instanceId, uv, normal ){
        if( !(this.addHorizontalDivider || this.addVerticalDivider) || !this.#mesh) return;
        
        const geom = this.#mesh.geometry;
        if(!geom) return;
        const attr = geom.getAttribute('dividerLines');
        if(!attr) return;
        
        const allowUv = instanceId >= 0 && uv && normal ? this.collideWithNormal(normal) : false;
        
        for(var i=0; i< this.#mesh.count; i++){
            const itemSize = attr.itemSize || 3;
            const offset = i * itemSize;
            if(i === instanceId && allowUv){
                attr.array[offset + 0] = uv.x;
                attr.array[offset + 1] = uv.y;
                attr.array[offset + 2] = this.addVerticalDivider ? 2.0 : this.addHorizontalDivider ? 1.0 : 0.0;
            }else{
                attr.array[offset + 0] = -1;
                attr.array[offset + 1] = -1;
                attr.array[offset + 2] = 0.0;
            }
        }
        attr.needsUpdate = true;
    }

    /**

     * @param {number} instanceId
     * @param {THREE.Vector2} uv
     * @param {THREE.Vector3} normal
     * @returns {THREE.Vector2|null} normalized coords in the instance (0..1)
     */
    getPositionFromInstanceUV( instanceId, uv, normal ){
        if(typeof instanceId !== 'number' || !uv || !normal) return null;

        var items = this.#locations.length == 0 ? [ this.#generateDefault() ] : new Array(this.#dividers.length).fill(null).concat(this.#locations);
        var item = items[ instanceId ];
        if(!item) return null;
        const deltaX = item.x + item.width * uv.x;
        const deltaY = item.y + item.height * uv.y;

        return new THREE.Vector2(deltaX, deltaY);
    }

    /**
     * 
     * @param {THREE.Vector3} normal 
     */
    collideWithNormal( normal ){
        if(!normal) return false;
        if(this.addHorizontalDivider){
            if(normal.y === 1 || normal.y === -1) return true;
            if(normal.z === 1 || normal.z === -1) return true;
        }else if( this.addVerticalDivider ){
            if(normal.x === 1 || normal.x === -1) return true;
            if(normal.z === 1 || normal.z === -1) return true;
        }
        return false;
    }

    

    /**
     * 
     * @param {THREE.Intersection} intersection
     * @return {PlanogramBlockingPosition|PlanogramBlockingDivider|null} 
     */
    getIntersectedBox( intersection ){
        if(!intersection  ) return null;
        var divider = this.#dividers.find( (item, index) => intersection.instanceId === index  );
        var location = this.#locations.find( (item, index) => intersection.instanceId === index + this.#dividers.length );
        return location || divider || null;
    }

    updateMeshes(){
        if(this.isConnected){
            this.#createMeshes();
        }
    }

    /**
     * Apply renderer-level behaviour for showing positions through blocking
     * @param {boolean} showOnTop
     */
    applyShowPositionsThroughBlocking(showOnTop){
        if(!this.#mesh || !this.#mesh.material) return;
        if(showOnTop){
            this.#mesh.material.depthWrite = false;
            this.#mesh.material.depthTest = true;
            this.#mesh.renderOrder = 0;
        }else{
            this.#mesh.material.depthWrite = true;
            this.#mesh.material.depthTest = true;
            this.#mesh.renderOrder = 12;
        }
    }

    readjustCentre(){
        const planogramBox = PlanogramContainer.get(this)?.planogramBox;
        if(planogramBox && this.#mesh){
            const planogramPosition = planogramBox.getCenter(new THREE.Vector3());
            const halfSize = planogramBox.getSize(new THREE.Vector3()).multiplyScalar(0.5);
            this.#mesh.position.copy(planogramPosition.sub(halfSize));
            this.#mesh.updateMatrixWorld();
        }
    }

    raiseClickEvent( intersection ){
        if((!this.addHorizontalDivider && !this.addVerticalDivider) || this.disabled ) return null;
        const renderer = PlanogramRenderer.get(this);
        const xy = this.getPositionFromInstanceUV(intersection.instanceId, intersection.uv, intersection.face.normal);
        if(!xy) return null;
        const clickEvent = new MouseEvent( "click", { bubbles: true, cancelable: true, view: window, clientX: xy.x * 1000, clientY: xy.y * 1000 });
        Object.defineProperty(clickEvent, 'button', { ...Object.getOwnPropertyDescriptor(clickEvent, 'button'), get: function() {return 0;}});
        Object.defineProperty(clickEvent, 'target', { ...Object.getOwnPropertyDescriptor(clickEvent, 'target'), get: (function() {return this;}).bind(this)});
        if( this.dispatchEvent(clickEvent) && renderer.showMessages ){
            console.groupCollapsed("PlanogramBlockingDivider Placed");
            console.log("Target: ", this);
            console.log("Event: ", clickEvent);
            console.groupEnd();
        }
    }

    connectedCallback() {
        this.#generateGeometry();
        this.#mesh = new THREE.InstancedMesh( this.#geometry, PlanogramRendererResources.BlockShader, 0 );
        super.connectedCallback();
        this.addMesh( this.#mesh );
        this.#createMeshes();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        if (this.firstRender || oldValue === newValue ) return;
        if(name === PlanogramBlocking.attributes.AddVerticalDivider || name === PlanogramBlocking.attributes.AddHorizontalDivider){
            if(!this.addHorizontalDivider && !this.addVerticalDivider) this.updateShaderAttributes(-1);
        }
        if(name === PlanogramBlocking.attributes.Disabled){
            this.updateShaderAttributes(-1);
        }
        if(name === PlanogramBlocking.attributes.PlanogramDimensions || name === PlanogramBlocking.attributes.Units){
            this.#generateGuideMesh();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    dispose(){
        this.#geometry?.dispose();
        this.#geometry = null;
        if(this.#mesh){
            this.#mesh.removeFromParent();
            if(this.#mesh.dispose)
                this.#mesh.dispose();
            this.#mesh = null;
        }
        this.#guideMesh.geometry.dispose();
        this.#guideMesh.removeFromParent();
        this.#guideMeshCanvasTexture.dispose();
        super.dispose();
    }

}